import { createContentfulGraphQLClient, useGetDynamicFormsData, locales } from '@dfds-frontend/contentful-api-calls';
import { Button, Spinner, GlobalStyles, Banner, fallbackLocale } from '@dfds-ui/react-components';
import { createContext, useMemo, useContext, useRef, useState, useEffect, createElement } from 'react';
import { getUserSegmentId, getGaClientId, splitLocale, track } from '@dfds-frontend/tracking';
import { useGoogleReCaptchaV3 } from '@dfds-ui/hooks';
import { useForm, FormProvider, useFormContext, useFormState, Controller } from 'react-hook-form';
import { styled, StyledItem, Grid, GridItem, Text } from '@dfds-frontend/hydro';
import { CheckboxGroup, Checkbox, RatingField, Radio, NativeSelectField, TelField, RadioGroup, TextareaField, TextField } from '@dfds-ui/forms';
import { AddressSelect, GooglePlacesAPIProvider } from '@dfds-ui/google-places';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import Markdown from 'markdown-to-jsx';
import { Star } from '@dfds-ui/icons/system';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/utils/index.ts
function sanitizeNameField(name) {
  if (!name)
    return "";
  name = name.replace(".", " ").replace("[", "(").replace("]", ")");
  return name.trim();
}
var stripNullishValues = (data) => {
  return Object.keys(data).reduce(
    (acc, key) => {
      if (data[key]) {
        acc[key] = data[key];
      }
      return acc;
    },
    {}
  );
};

// src/trackData/trackDataHelpers.ts
var getFormConfigTrackingEventProps = (formModel, formPayload) => {
  var _a, _b;
  const trackingEventProperties = (_b = (_a = formModel.configuration) == null ? void 0 : _a.trackingEventProperties) != null ? _b : [];
  const obj = {};
  trackingEventProperties.forEach((p) => {
    const fieldValue = formPayload == null ? void 0 : formPayload[p.sourceFieldName];
    if (fieldValue) {
      obj[p.property] = typeof fieldValue === "string" ? fieldValue : fieldValue.value;
    }
  });
  return obj;
};
var splitFullNameFieldValue = (fullName) => {
  const splittedValues = {
    firstName: void 0,
    lastName: void 0
  };
  if (fullName) {
    const fullNameArray = fullName.split(" ");
    const lastItemIndex = fullNameArray.length - 1;
    splittedValues.firstName = fullNameArray.slice(0, lastItemIndex).join(" ");
    splittedValues.lastName = fullNameArray[lastItemIndex];
  }
  return splittedValues;
};

// src/trackData/buildTrackData.ts
var buildTrackData = ({
  event,
  countryCode,
  languageCode,
  trackingDataBase,
  formModel,
  formPayload,
  error
}) => {
  var _a, _b, _c, _d, _e;
  const sharedTrackingData = __spreadProps(__spreadValues({}, trackingDataBase), {
    countryCode,
    languageCode,
    formName: (_a = formModel.trackingName) != null ? _a : "",
    formID: (_b = formModel.sys.id) != null ? _b : "",
    stage: (_c = formModel.trackingStage) != null ? _c : void 0,
    conversionPoint: (_d = formModel.trackingConversionPoint) != null ? _d : void 0
  });
  const valueToSplit = (formPayload == null ? void 0 : formPayload.FullName) || (formPayload == null ? void 0 : formPayload.ContactPersonName) || "";
  const { firstName, lastName } = splitFullNameFieldValue(
    valueToSplit
  );
  const formDataTracking = stripNullishValues({
    companyName: formPayload == null ? void 0 : formPayload.CompanyName,
    companyLocation: (_e = formPayload == null ? void 0 : formPayload.CompanyLocation) == null ? void 0 : _e.value,
    companyEmail: (formPayload == null ? void 0 : formPayload.CompanyEmail) || (formPayload == null ? void 0 : formPayload.ContactPersonEmail),
    companyPhoneNumber: formPayload == null ? void 0 : formPayload.PhoneNumber,
    customerEmail: formPayload == null ? void 0 : formPayload.PersonalEmail,
    firstName: (formPayload == null ? void 0 : formPayload.FirstName) || firstName,
    lastName: (formPayload == null ? void 0 : formPayload.LastName) || lastName
  });
  return __spreadValues(__spreadProps(__spreadValues(__spreadValues(__spreadValues({
    event
  }, sharedTrackingData), formDataTracking), getFormConfigTrackingEventProps(formModel, formPayload)), {
    form_payload: JSON.stringify(formPayload)
  }), error != null ? error : {});
};
var trackData = ({
  disableTracking,
  event,
  trackingDataBase,
  formModel,
  formPayload,
  error
}) => {
  var _a;
  if (disableTracking)
    return;
  const { languageCode, countryCode } = splitLocale(
    (_a = trackingDataBase.currentLocale) != null ? _a : ""
  );
  const data = buildTrackData({
    event,
    languageCode,
    countryCode,
    trackingDataBase,
    formModel,
    formPayload,
    error
  });
  return track(data);
};
var DynamicFormContext = createContext(null);
var useDynamicForm = () => {
  const ctx = useContext(DynamicFormContext);
  if (ctx === null) {
    throw new Error(
      "useDynamicForm must be inside a DynamicFormContext.Provider with a value"
    );
  }
  return ctx;
};
var StyledForm = styled("form", {
  fontFamily: "$DFDS",
  a: {
    color: "$navy-medium"
  },
  label: {
    marginBottom: "$xxs"
  },
  'label, ::placeholder, input, textarea, select, .react-select__placeholder, fieldset > span, textfield, span[class*="TelField"]': {
    fontFamily: "$DFDS !important",
    fontSize: "15px !important",
    lineHeight: "$label-medium"
  },
  "fieldset > div": {
    gap: "$xs"
  },
  "::placeholder, .react-select__placeholder": {
    color: "$group-blue-disabled !important"
  },
  '*[class*="AssistiveText"]': {
    fontFamily: "$DFDS",
    color: "$group-blue-secondary",
    fontSize: "$overline"
  },
  ".error": {
    i: {
      border: "2px solid #be1e2d"
    }
  },
  [`${StyledItem}`]: {
    display: "flex",
    height: "100%"
  }
});
var RowStyled = styled(Grid, {
  gap: "0 $m"
});
var FieldStyled = styled("div", {
  display: "flex",
  flexDirection: "column",
  marginBottom: "$s",
  flexGrow: 1
});
var ButtonStyled = styled(Button, {
  marginTop: "$m"
});
var AddressSelectStyled = styled(AddressSelect, {
  ".react-select__control": {
    height: "40px !important"
  },
  fontSize: "$body-1",
  "> div:last-of-type": {
    fontFamily: "$DFDS",
    color: "$group-blue-secondary",
    fontSize: "$overline",
    marginTop: "$xxs"
  }
});
var CheckboxGroupStyled = styled(CheckboxGroup, {
  span: {
    fontSize: "$overline"
  }
});
var CheckboxStyled = styled(Checkbox, {
  span: {
    fontSize: "$subtitle-2 !important",
    fontFamily: "$DFDS"
  }
});
var ComponentStyled = styled("div", {
  "button + div": {
    minWidth: "auto"
  }
});
var MarkdownStyled = styled("div", {
  marginBottom: "$s"
});
var QuestionStyled = styled("label", {
  color: "$group-blue-primary",
  variants: {
    required: {
      true: {
        "&::after": {
          content: " *",
          color: "#49A2DF"
        }
      }
    }
  }
});
var DividerStyled = styled("hr", {
  margin: "$l $s",
  border: 0,
  height: "1px",
  background: "$divider-dark"
});
var RatingStyled = styled(RatingField, {
  span: {
    fontFamily: "$DFDS"
  }
});
var RadioStyled = styled(Radio, {
  label: {
    margin: 0
  },
  span: {
    fontFamily: "$DFDS"
  }
});
var SelectStyled = styled(NativeSelectField, {});
var MessageStyled = styled("div", {
  textAlign: "center",
  fontFamily: "$DFDS",
  a: {
    color: "$navy-medium",
    textDecoration: "none"
  },
  p: {
    fontSize: "$body-1"
  },
  "& h1": {
    textAlign: "center",
    fontSize: "100px"
  }
});

// src/components/expressions/expressions.ts
var truePredicate = () => true;
function notExpression(predicate) {
  return predicate() === false;
}
function createFieldValuePredicate(fieldValueCondition, valueGetter) {
  return () => {
    return valueGetter(fieldValueCondition.fieldName) === fieldValueCondition.fieldValue;
  };
}
function createExpressionExecuter(expression, valueGetter) {
  var _a;
  if (expression === void 0) {
    return truePredicate;
  }
  const op = (_a = expression.operation) != null ? _a : expression.kind;
  if (expression.conditions.length === 0) {
    throw Error("Cannot create expression executer without any conditions.");
  }
  if (op === "eq") {
    return createFieldValuePredicate(expression.conditions[0], valueGetter);
  }
  if (op === "not") {
    return () => notExpression(
      createFieldValuePredicate(expression.conditions[0], valueGetter)
    );
  }
  if (op === "and" || op === "or") {
    throw Error("Kind and/or not implemented");
  }
  return truePredicate;
}
var FormAddressSelect = ({
  question,
  placeholder,
  id,
  assistiveText,
  errorMessage,
  required
}) => {
  const formContext = useDynamicForm();
  const { control } = useFormContext();
  if (!id)
    return null;
  if (!(formContext == null ? void 0 : formContext.googleMapsApiKey)) {
    throw new Error("Google Maps Api Key is missing from formContext");
  }
  return /* @__PURE__ */ jsx(
    Controller,
    {
      control,
      name: id,
      rules: {
        required: {
          value: !!required,
          message: errorMessage || ""
        }
      },
      render: ({ field, fieldState }) => {
        return /* @__PURE__ */ jsxs(
          GooglePlacesAPIProvider,
          {
            apiKey: formContext.googleMapsApiKey,
            libraries: "geometry,drawing,places",
            children: [
              /* @__PURE__ */ jsx(QuestionStyled, { required: !!required, children: question }),
              /* @__PURE__ */ jsx(
                AddressSelectStyled,
                {
                  onChange: field.onChange,
                  onBlur: field.onBlur,
                  isClearable: true,
                  name: id,
                  placeholder: placeholder || "",
                  assistiveText: assistiveText || void 0,
                  error: fieldState.error && errorMessage || void 0
                }
              )
            ]
          }
        );
      }
    }
  );
};
var emailRegExp = (
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);
var FormInputField = (_a) => {
  var _b = _a, {
    id,
    question,
    placeholder,
    assistiveText,
    errorMessage,
    required,
    inputType,
    locale
  } = _b, rest = __objRest(_b, [
    "id",
    "question",
    "placeholder",
    "assistiveText",
    "errorMessage",
    "required",
    "inputType",
    "locale"
  ]);
  var _a2, _b2;
  const { register, control } = useFormContext();
  const { errors } = useFormState();
  if (!id)
    return null;
  const type = inputType == null ? void 0 : inputType.toLowerCase();
  if (type === "phone") {
    const telFieldLocale = (_b2 = (_a2 = locales.find(
      (l) => l.locale.toLocaleLowerCase() === locale.toLocaleLowerCase()
    )) == null ? void 0 : _a2.locale) != null ? _b2 : fallbackLocale;
    return /* @__PURE__ */ jsx(
      Controller,
      {
        control,
        name: id,
        rules: {
          required: {
            value: !!required,
            message: errorMessage || ""
          }
        },
        render: ({ field: { onChange, onBlur, ref } }) => {
          return /* @__PURE__ */ jsx(ComponentStyled, { children: /* @__PURE__ */ jsx(
            TelField,
            {
              name: id,
              label: question,
              errorMessage: errors[id] && errorMessage || "",
              required: !!required,
              defaultLocale: telFieldLocale,
              onChange,
              onBlur,
              ref,
              assistiveText: assistiveText || ""
            }
          ) });
        }
      }
    );
  }
  const Component = type === "long text" ? TextareaField : TextField;
  return /* @__PURE__ */ jsx(
    ComponentStyled,
    __spreadValues(__spreadValues(__spreadProps(__spreadValues({}, rest), {
      as: Component,
      label: question,
      assistiveText: assistiveText || "",
      placeholder: placeholder || "",
      errorMessage: errors[id] && errorMessage || "",
      className: errors[id] ? "error" : "",
      required: !!required
    }), register(id, {
      required: {
        value: !!required,
        message: errorMessage || ""
      },
      pattern: {
        value: type === "email" ? emailRegExp : /(?:)/,
        message: errorMessage || ""
      }
    })), { inputType: type })
  );
};
var MarkdownText = ({ text, newWindow }) => {
  if (!text)
    return null;
  return /* @__PURE__ */ jsx(
    Markdown,
    {
      options: {
        overrides: {
          a: {
            component: (_a) => {
              var _b = _a, { children } = _b, rest = __objRest(_b, ["children"]);
              return /* @__PURE__ */ jsx("a", __spreadProps(__spreadValues({ target: newWindow ? "_blank" : void 0 }, rest), { children }));
            }
          },
          //TODO: Drop the support for h1 in markdown and here as well
          h1: {
            component: (_c) => {
              var props = __objRest(_c, []);
              return /* @__PURE__ */ jsx(Text, __spreadValues({ as: "h2", variant: "headline-2" }, props));
            }
          },
          h2: {
            component: (_d) => {
              var props = __objRest(_d, []);
              return /* @__PURE__ */ jsx(Text, __spreadValues({ as: "h2", variant: "headline-2" }, props));
            }
          },
          h3: {
            component: (_e) => {
              var props = __objRest(_e, []);
              return /* @__PURE__ */ jsx(Text, __spreadValues({ as: "h3", variant: "label-medium-bold" }, props));
            }
          },
          p: {
            component: (_f) => {
              var props = __objRest(_f, []);
              return /* @__PURE__ */ jsx(Text, __spreadValues({ as: "p", variant: "body-1" }, props));
            }
          },
          hr: {
            component: (_g) => {
              var props = __objRest(_g, []);
              return /* @__PURE__ */ jsx(DividerStyled, __spreadValues({}, props));
            }
          }
        }
      },
      children: text
    }
  );
};
var FormMarkdownField = ({ text }) => {
  if (!text)
    return null;
  return /* @__PURE__ */ jsx(MarkdownStyled, { children: /* @__PURE__ */ jsx(MarkdownText, { text }) });
};
var FormRatingField = ({
  question,
  id,
  assistiveText,
  errorMessage,
  counter,
  required
}) => {
  const { register, control } = useFormContext();
  const { errors } = useFormState();
  if (!id)
    return null;
  const _a = register(id), { onChange } = _a, restRegister = __objRest(_a, ["onChange"]);
  return /* @__PURE__ */ jsx(
    Controller,
    {
      control,
      name: id,
      rules: {
        required: {
          value: !!required,
          message: errorMessage || ""
        }
      },
      render: () => {
        return /* @__PURE__ */ jsx(
          RatingStyled,
          __spreadValues({
            rangeCardinality: counter || 5,
            errorMessage: errors[id] && errorMessage || void 0,
            assistiveText: assistiveText != null ? assistiveText : "",
            label: question,
            required: !!required,
            size: "medium",
            className: errors[id] ? "error" : "",
            cumulative: true,
            Icon: () => Star,
            onChange: (_, e) => {
              onChange(e);
            }
          }, restRegister)
        );
      }
    }
  );
};
var FormSelectField = (_a) => {
  var _b = _a, {
    question,
    id,
    inputType,
    assistiveText,
    placeholder,
    errorMessage,
    optionsCollection,
    required = false
  } = _b, rest = __objRest(_b, [
    "question",
    "id",
    "inputType",
    "assistiveText",
    "placeholder",
    "errorMessage",
    "optionsCollection",
    "required"
  ]);
  const { register, control } = useFormContext();
  const { errors } = useFormState();
  if (!id)
    return null;
  const type = inputType == null ? void 0 : inputType.toLowerCase();
  if (type === "column radio" || type === "row radio")
    return /* @__PURE__ */ jsx(
      RadioGroup,
      {
        visualSize: "medium",
        label: question,
        required: !!required,
        column: type === "column radio",
        errorMessage: errors[id] && errorMessage || void 0,
        children: optionsCollection == null ? void 0 : optionsCollection.items.map((option) => {
          if (!option)
            return null;
          const { displayValue, value } = option;
          return /* @__PURE__ */ jsx(
            RadioStyled,
            __spreadValues({
              label: displayValue,
              value: value || ""
            }, register(id, {
              required: {
                value: !!required,
                message: errorMessage || ""
              }
            })),
            value || ""
          );
        })
      }
    );
  return /* @__PURE__ */ jsx(
    Controller,
    {
      control,
      name: id,
      rules: {
        required: {
          value: !!required,
          message: errorMessage || ""
        }
      },
      render: ({ field: { name, onChange } }) => {
        return /* @__PURE__ */ jsxs(
          SelectStyled,
          __spreadProps(__spreadValues({
            label: question,
            placeholder: placeholder || "",
            name,
            assistiveText: assistiveText || "",
            errorMessage: errors[name] && errorMessage || "",
            required: !!required,
            onChange
          }, rest), {
            children: [
              /* @__PURE__ */ jsx("option", { selected: true, disabled: !!required, hidden: !!required, children: placeholder }),
              optionsCollection == null ? void 0 : optionsCollection.items.map((option) => {
                if (!option)
                  return null;
                const { displayValue, value } = option;
                return /* @__PURE__ */ jsx(
                  "option",
                  {
                    label: displayValue,
                    value: value || ""
                  },
                  value || ""
                );
              })
            ]
          })
        );
      }
    }
  );
};
var FormMultiSelectCheckboxField = ({
  question,
  id,
  optionsCollection,
  errorMessage,
  required
}) => {
  var _a, _c;
  const { register, watch } = useFormContext();
  const { errors } = useFormState();
  if (!id || !(optionsCollection == null ? void 0 : optionsCollection.items.length))
    return null;
  const singleCheckboxItem = (optionsCollection == null ? void 0 : optionsCollection.items.length) === 1;
  const defaultValue = singleCheckboxItem ? "false" : [];
  const _b = register(id, {
    value: defaultValue,
    required: {
      value: !!required,
      message: ((_a = errors[id]) == null ? void 0 : _a.message) || ""
    }
  }), { onChange } = _b, restRegister = __objRest(_b, ["onChange"]);
  const checkedOptions = watch(id, defaultValue);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    !!question && /* @__PURE__ */ jsx(QuestionStyled, { required: !!required, children: question }),
    /* @__PURE__ */ jsx(
      CheckboxGroupStyled,
      {
        errorMessage: errors[id] && errorMessage || void 0,
        error: errors[id] != void 0,
        children: (_c = optionsCollection == null ? void 0 : optionsCollection.items) == null ? void 0 : _c.map((formSelectOption) => {
          if (!(formSelectOption == null ? void 0 : formSelectOption.value))
            return;
          const value = singleCheckboxItem ? "true" : formSelectOption.value;
          return /* @__PURE__ */ jsx(
            CheckboxStyled,
            __spreadProps(__spreadValues({
              value,
              checked: singleCheckboxItem ? checkedOptions === "true" : checkedOptions.includes(value),
              error: !!errors[id],
              onChange
            }, restRegister), {
              children: formSelectOption == null ? void 0 : formSelectOption.displayValue
            }),
            formSelectOption.value
          );
        })
      }
    )
  ] });
};
var FieldController = ({
  field: { sys, field: fieldItem, question = "", required = false },
  expressions,
  methods: { getValues, watch },
  locale
}) => {
  var _a;
  if (!fieldItem)
    return null;
  const { inputType, id } = fieldItem;
  const type = inputType == null ? void 0 : inputType.toLowerCase();
  const visibilityExpression = (_a = expressions.find((exp) => {
    if (!id || ![
      "text",
      "dropdown",
      "column radio",
      "row radio",
      "star rating"
    ].includes(type)) {
      return false;
    }
    if (Array.isArray(exp.fieldName)) {
      return exp.fieldName.includes(id);
    }
    return id === exp.fieldName;
  })) == null ? void 0 : _a.visibility;
  if (visibilityExpression) {
    watch(visibilityExpression.conditions.map((c) => c.fieldName));
  }
  const isVisible = createExpressionExecuter(visibilityExpression, getValues)();
  if (!isVisible) {
    return null;
  }
  const extendedField = __spreadProps(__spreadValues({ locale }, fieldItem), { question, required });
  switch (type) {
    case "address":
      return /* @__PURE__ */ createElement(FormAddressSelect, __spreadProps(__spreadValues({}, extendedField), { key: sys.id }));
    case "text":
    case "phone":
    case "email":
    case "number":
    case "long text":
    case "date":
      return /* @__PURE__ */ createElement(FormInputField, __spreadProps(__spreadValues({}, extendedField), { key: sys == null ? void 0 : sys.id }));
    case "dropdown":
    case "column radio":
    case "row radio":
      return /* @__PURE__ */ createElement(FormSelectField, __spreadProps(__spreadValues({}, extendedField), { key: sys.id }));
    case "star rating":
      return /* @__PURE__ */ createElement(FormRatingField, __spreadProps(__spreadValues({}, extendedField), { key: sys.id }));
    case "multiselectcheckbox":
    case "checkbox":
      return /* @__PURE__ */ createElement(FormMultiSelectCheckboxField, __spreadProps(__spreadValues({}, extendedField), { key: sys.id }));
    default:
      return /* @__PURE__ */ jsx(Fragment, { children: inputType }) ;
  }
};
var FormRow = (_a) => {
  var _b = _a, { columnsCollection } = _b, props = __objRest(_b, ["columnsCollection"]);
  const columnSize = 12 / ((columnsCollection == null ? void 0 : columnsCollection.items.length) || 1);
  return /* @__PURE__ */ jsx(RowStyled, { noMargin: true, children: columnsCollection == null ? void 0 : columnsCollection.items.map((field) => {
    var _a2;
    if (!field)
      return null;
    return /* @__PURE__ */ jsx(
      GridItem,
      {
        small: 12,
        medium: columnSize,
        large: columnSize,
        children: /* @__PURE__ */ jsx(FieldStyled, { children: /* @__PURE__ */ jsx(FieldController, __spreadValues({ field }, props)) })
      },
      (_a2 = field == null ? void 0 : field.sys) == null ? void 0 : _a2.id
    );
  }) });
};
var Wrapper = ({ children }) => {
  return /* @__PURE__ */ jsx(MessageStyled, { children });
};
var MessageBanner = ({ children }) => {
  return /* @__PURE__ */ jsx(
    Markdown,
    {
      options: {
        wrapper: Wrapper,
        forceWrapper: true
      },
      children
    }
  );
};
var SubmitErrorBanner = ({ children }) => {
  return /* @__PURE__ */ jsx(Banner, { intent: "critical", children: /* @__PURE__ */ jsx(Markdown, { children }) });
};
var FormConsentCheckboxField = (_a) => {
  var _b = _a, {
    name: unsanitizedName,
    label,
    errorMessage,
    isRequired
  } = _b, props = __objRest(_b, [
    "name",
    "label",
    "errorMessage",
    "isRequired"
  ]);
  var _b2;
  const name = sanitizeNameField(unsanitizedName);
  const { register } = useFormContext();
  const { errors } = useFormState();
  const { showConsentPolicyLinksInNewWindow } = useDynamicForm() || {};
  const [checked, setChecked] = useState(false);
  const _a2 = register(name, {
    required: {
      value: isRequired ? isRequired : checked,
      message: errorMessage || ""
    }
  }), { onChange } = _a2, restRegister = __objRest(_a2, ["onChange"]);
  return /* @__PURE__ */ jsx(
    CheckboxGroupStyled,
    {
      errorMessage: (_b2 = errors[name]) == null ? void 0 : _b2.message,
      error: errors[name] != void 0,
      children: /* @__PURE__ */ jsx(
        Checkbox,
        __spreadProps(__spreadValues(__spreadValues({
          inputVerticalAlignment: "top",
          checked
        }, props), restRegister), {
          onChange: (e) => {
            setChecked(e.target.checked);
            onChange(e);
          },
          children: /* @__PURE__ */ jsx(
            MarkdownText,
            {
              newWindow: showConsentPolicyLinksInNewWindow,
              text: label || ""
            }
          )
        })
      )
    }
  );
};
var SITE_KEY = "6LdITbAaAAAAABwOyHhgbq0RpGPu2_t0jmLXpLcM";
var createSubmitHandler = (endpoint = "") => {
  return async (data) => {
    const response = await fetch(endpoint, {
      method: "POST",
      body: JSON.stringify({ data }),
      headers: {
        "Content-Type": "application/json"
      }
    }).catch((error) => {
      return Promise.reject({
        correlationId: "",
        message: error.message,
        errorCode: 1001 /* NETWORK_ERROR */
      });
    });
    const { message, correlationId, errorCode } = await response.json();
    if (!response.ok) {
      return Promise.reject({ correlationId, message, errorCode });
    }
    return correlationId;
  };
};
var DynamicFormView = (_a) => {
  var _b = _a, {
    form,
    locale,
    submitEndpoint,
    disableReCaptcha = false,
    shouldUnregister = true,
    onFirstRender,
    onSubmitSuccess,
    onSubmitError,
    onValidationError
  } = _b, rest = __objRest(_b, [
    "form",
    "locale",
    "submitEndpoint",
    "disableReCaptcha",
    "shouldUnregister",
    "onFirstRender",
    "onSubmitSuccess",
    "onSubmitError",
    "onValidationError"
  ]);
  var _a2, _b2, _c, _d, _e, _f, _g, _h;
  const firstFormRender = useRef(true);
  const formID = form.sys.id;
  const formTitle = (_a2 = form.entryTitle) != null ? _a2 : `untitled (${form.sys.id})`;
  const commercialOwner = (_b2 = form.commercialOwner) != null ? _b2 : "";
  const [isSuccessful, setIsSuccessful] = useState(false);
  const methods = useForm({
    shouldUnregister
  });
  const executeReCaptcha = useGoogleReCaptchaV3({
    siteKey: SITE_KEY,
    disabled: disableReCaptcha
  });
  const {
    formState: { isSubmitted, isSubmitting, isSubmitSuccessful },
    register,
    getValues,
    watch
  } = methods;
  useEffect(() => {
    if (firstFormRender.current) {
      onFirstRender == null ? void 0 : onFirstRender();
    }
    firstFormRender.current = false;
  }, [onFirstRender]);
  const { businessArea } = useDynamicForm() || {};
  const userSegmentID = getUserSegmentId();
  const {
    dfdsUserId: segmentId = "",
    anonymousUserId: anonymousSegmentId = ""
  } = userSegmentID;
  const onSubmit = async (formPayload) => {
    try {
      const reCaptchaToken = await executeReCaptcha("submit");
      await createSubmitHandler(submitEndpoint)(__spreadProps(__spreadValues({}, formPayload), {
        clientSessionId: getGaClientId(),
        userSegmentID,
        formVariant: variantResolver(),
        consentData: form.consentFieldsCollection,
        reCaptchaToken,
        referer: window.location.href
      }));
      setIsSuccessful(true);
      onSubmitSuccess == null ? void 0 : onSubmitSuccess(formPayload);
    } catch (e) {
      const { message, correlationId, errorCode } = e;
      setIsSuccessful(false);
      const errors = {
        errorMessage: message || "",
        errorMessageId: errorCode || "",
        errorCorrelationId: correlationId || ""
      };
      onSubmitError == null ? void 0 : onSubmitError(errors, formPayload);
    }
  };
  const onError = (errors) => {
    onValidationError == null ? void 0 : onValidationError(
      Object.keys(errors).map((formElementName) => {
        var _a3, _b3;
        return {
          errorElementName: formElementName,
          errorMessageId: (_a3 = errors[formElementName]) == null ? void 0 : _a3.type,
          errorMessage: (_b3 = errors[formElementName]) == null ? void 0 : _b3.message,
          errorFieldValue: getValues(formElementName)
        };
      })
    );
  };
  const variantResolver = () => {
    var _a3, _b3;
    return ((_b3 = (_a3 = form.configuration) == null ? void 0 : _a3.variantResolver) == null ? void 0 : _b3.fieldName) ? getValues(form.configuration.variantResolver.fieldName) : null;
  };
  const expressions = useMemo(
    () => {
      var _a3, _b3;
      return (_b3 = (_a3 = form.configuration) == null ? void 0 : _a3.expressions) != null ? _b3 : [];
    },
    [form.configuration]
  );
  if (isSubmitted && isSuccessful) {
    return /* @__PURE__ */ jsx(MessageBanner, { children: ((_c = form.submitSuccessMessage) == null ? void 0 : _c.text) || "" });
  }
  return /* @__PURE__ */ jsx(FormProvider, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsxs(
    StyledForm,
    __spreadProps(__spreadValues({
      onSubmit: methods.handleSubmit(onSubmit, onError),
      "data-form-id": formID
    }, rest), {
      children: [
        (_e = (_d = form == null ? void 0 : form.rowsCollection) == null ? void 0 : _d.items) == null ? void 0 : _e.map((row) => {
          if (!row)
            return null;
          const { sys, __typename } = row;
          if (__typename === "FormMarkdownField")
            return /* @__PURE__ */ jsx(
              FormMarkdownField,
              __spreadValues({}, row),
              sys.id
            );
          return /* @__PURE__ */ jsx(
            FormRow,
            __spreadValues({
              expressions,
              methods: { getValues, watch },
              locale
            }, row),
            sys.id
          );
        }),
        (_f = form.consentFieldsCollection) == null ? void 0 : _f.items.map((field, index) => {
          var _a3;
          if (!field)
            return null;
          const isRequired = field.__typename === "FormConsentCheckboxField";
          return /* @__PURE__ */ createElement(
            FormConsentCheckboxField,
            __spreadProps(__spreadValues({}, field), {
              key: "sys" in field && ((_a3 = field.sys) == null ? void 0 : _a3.id) || `consent-field-${index}`,
              locale: locale || "en",
              isRequired
            })
          );
        }),
        /* @__PURE__ */ jsx("input", __spreadValues({ id: "id", type: "hidden", value: formID }, register("formId"))),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "formTitle",
            type: "hidden",
            value: formTitle
          }, register("formTitle"))
        ),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "formLocale",
            type: "hidden",
            value: locale != null ? locale : "en"
          }, register("formLocale"))
        ),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "commercialOwner",
            type: "hidden",
            value: commercialOwner
          }, register("commercialOwner"))
        ),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "client-session-id",
            type: "hidden"
          }, register("clientSessionId"))
        ),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "businessArea",
            type: "hidden",
            value: businessArea
          }, register("businessArea"))
        ),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "segmentId",
            type: "hidden",
            value: segmentId
          }, register("segmentId"))
        ),
        /* @__PURE__ */ jsx(
          "input",
          __spreadValues({
            id: "anonymousSegmentId",
            type: "hidden",
            value: anonymousSegmentId
          }, register("anonymousSegmentId"))
        ),
        isSubmitted && !isSuccessful && isSubmitSuccessful && /* @__PURE__ */ jsx(SubmitErrorBanner, { children: ((_g = form.submitFailureMessage) == null ? void 0 : _g.text) || "Unknown data error" }),
        /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
          ButtonStyled,
          {
            type: "submit",
            disabled: isSubmitting,
            submitting: isSubmitting,
            children: (_h = form.submitButton) == null ? void 0 : _h.label
          }
        ) })
      ]
    })
  ) }));
};
var DynamicFormController = ({
  disableTracking,
  disableReCaptcha,
  shouldUnregister
}) => {
  var _a, _b;
  const {
    form: formModel,
    locale,
    submitEndpoint,
    bookingEngine = "dotcom",
    businessArea,
    pageID
  } = useDynamicForm() || {};
  if (!locale || !formModel || !submitEndpoint)
    return /* @__PURE__ */ jsx(Fragment, {});
  const trackingDataBase = {
    pageID,
    bookingEngine,
    businessArea: businessArea == null ? void 0 : businessArea.toLowerCase()
  };
  const handleFirstRender = () => {
    trackData({
      disableTracking,
      event: "formRender" /* RENDER */,
      trackingDataBase,
      formModel
    });
  };
  const handleSubmitSuccess = (formPayload) => {
    trackData({
      disableTracking,
      event: "formSubmit" /* SUBMIT */,
      trackingDataBase,
      formModel,
      formPayload
    });
  };
  const handleSubmitError = (errorOnApiFailure, formPayload) => {
    trackData({
      disableTracking,
      event: "formSubmit" /* SUBMIT */,
      trackingDataBase,
      formModel,
      formPayload,
      error: errorOnApiFailure
    });
  };
  const handleValidationError = (validationErrors) => {
    if (disableTracking || validationErrors.length === 0)
      return;
    validationErrors.forEach(
      (error) => trackData({
        disableTracking,
        event: "formValidationError" /* VALIDATION */,
        trackingDataBase,
        formModel,
        formPayload: void 0,
        error
      })
    );
  };
  const trackingDataAttributes = {
    "data-contentfulid": formModel.sys.id,
    "data-stage": (_a = formModel.trackingStage) != null ? _a : void 0,
    "data-conversion-point": (_b = formModel.trackingConversionPoint) != null ? _b : void 0,
    "data-conversion-type": void 0
  };
  return /* @__PURE__ */ jsx(
    DynamicFormView,
    __spreadValues({
      form: formModel,
      locale,
      disableReCaptcha,
      shouldUnregister,
      submitEndpoint,
      onFirstRender: handleFirstRender,
      onSubmitSuccess: handleSubmitSuccess,
      onSubmitError: handleSubmitError,
      onValidationError: handleValidationError
    }, trackingDataAttributes)
  );
};
var DynamicForm = ({
  submitEndpoint,
  id,
  locale = "en",
  googleMapsApiKey,
  businessArea,
  bookingEngine,
  config: {
    clientArgs: { env, token }
  },
  disableTracking,
  disableReCaptcha,
  shouldUnregister,
  pageID,
  showConsentPolicyLinksInNewWindow = false,
  preview,
  includeGlobalStyles = false
}) => {
  const client = createContentfulGraphQLClient("z860498abl84", env, token);
  const { loading, error, form } = useGetDynamicFormsData({
    client,
    id,
    locale,
    preview: preview != null ? preview : false
  });
  const contextValue = useMemo(
    () => ({
      form,
      googleMapsApiKey,
      submitEndpoint,
      locale,
      businessArea,
      bookingEngine,
      showConsentPolicyLinksInNewWindow,
      pageID
    }),
    [
      form,
      googleMapsApiKey,
      submitEndpoint,
      locale,
      businessArea,
      bookingEngine,
      showConsentPolicyLinksInNewWindow,
      pageID
    ]
  );
  if (loading) {
    return /* @__PURE__ */ jsx(Spinner, {});
  }
  if (!form) {
    return /* @__PURE__ */ jsx("div", { children: "Form not found" });
  }
  if (error) {
    return /* @__PURE__ */ jsx("div", { children: "Error loading form" });
  }
  return /* @__PURE__ */ jsxs(DynamicFormContext.Provider, { value: contextValue, children: [
    includeGlobalStyles && /* @__PURE__ */ jsx(GlobalStyles, {}),
    /* @__PURE__ */ jsx(
      DynamicFormController,
      {
        disableTracking,
        disableReCaptcha,
        shouldUnregister
      }
    )
  ] });
};

export { DynamicForm };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.mjs.map